import { useState, MouseEvent } from 'react';
import { toast } from 'sonner';
import { Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import Facebook from '../Icons/Facebook';
import Twitter from '../Icons/Twitter';
// import Pinterest from '../Icons/Pinterest';
// import Reddit from '../Icons/Reddit';

const socialPlatforms = [
  {
    name: 'Facebook',
    icon: <Facebook />,
    url: 'https://www.facebook.com/sharer/sharer.php?u=',
  },
  {
    name: 'X',
    icon: <Twitter />,
    url: 'https://twitter.com/intent/tweet?url=',
  },
  // {
  //   name: 'Telegram',
  //   icon: <Telegram />,
  //   url: 'https://t.me/share/url?url=',
  // },
  //   {
  //     name: 'Pinterest',
  //     icon: <Pinterest />,
  //     url: 'https://www.pinterest.com/pin/create/button/?url=',
  //   },
  //   {
  //     name: 'Reddit',
  //     icon: <Reddit />,
  //     url: 'https://www.reddit.com/submit?url=',
  //   },
  //   {
  //     name: 'WhatsApp',
  //     icon: '/whatsapp-icon.svg',
  //     url: 'https://api.whatsapp.com/send?text=',
  //   },
];

function WebShare({
  shareUrl,
  shareTitle,
  shareText,
}: {
  shareUrl: string;
  shareTitle?: string;
  shareText?: string;
}) {
  if (!navigator.share) {
    return <div />;
  }

  const handleWebShare = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      await navigator.share({
        title: shareTitle ?? 'Filefinity',
        text: shareText ?? 'Check out this amazing file sharing platform!',
        url: shareUrl,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error sharing:', error);
    }
  };

  return (
    <Button
      variant="outline"
      type="button"
      onClick={handleWebShare}
      className="rounded-lg hover:bg-accent border-[#EAECF0] border-[1.5px] p-3"
    >
      <Share2 />
    </Button>
  );
}

function ShareButton({
  shareUrl,
  size = 'sm',
  shareTitle,
  shareText,
}: {
  shareUrl: string;
  size?: 'sm' | 'md' | 'lg' | 'icon';
  shareTitle?: string;
  shareText?: string;
}) {
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);

  const handleShare = (platform: string, e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    const platformDetails = socialPlatforms.find((p) => p.name === platform);

    if (platformDetails) {
      const shareLink = platformDetails.url + encodeURIComponent(shareUrl);
      window.open(shareLink, '_blank');
    }
  };

  const copyLink = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    navigator.clipboard.writeText(shareUrl);
    toast.success('Copied URL to clipboard');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size={size}
          // Icon mode drops the label; this stays the accessible name.
          aria-label="Share"
          title="Share"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setOpen(true);
          }}
        >
          <div className="flex items-center gap-1 justify-center">
            <Share2 size={size === 'icon' ? 20 : 16} />
            {size !== 'icon' && <p>Share</p>}
          </div>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" closeDialog={() => setOpen(false)}>
        <DialogHeader>
          <DialogTitle className="text-[20px] sm:text-2xl font-primary text-left">
            Share with your friends!
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col space-y-4">
          <p className="text-sm text-muted-foreground font-secondary">
            Share this post via
          </p>
          <div className="flex flex-wrap gap-4">
            {socialPlatforms.map((platform) => (
              <Button
                variant="outline"
                type="button"
                key={platform.name}
                onClick={(e) => handleShare(platform.name, e)}
                className="rounded-lg hover:bg-accent border-[#EAECF0] border-[1.5px] p-3 min-w-[44px]"
              >
                {platform.icon}
              </Button>
            ))}
            <WebShare
              shareUrl={shareUrl}
              shareTitle={shareTitle}
              shareText={shareText}
            />
          </div>
          <p className="text-sm text-muted-foreground font-secondary">
            or copy link
          </p>
          <div className="flex items-center space-x-2">
            <Input
              value={shareUrl}
              readOnly
              onFocus={(e) => e.currentTarget.select()}
              className="min-w-0 flex-grow font-tertiary text-xs"
            />
            <Button onClick={copyLink} variant="default">
              {copied ? 'Copied!' : 'Copy'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ShareButton;
